import { useState, type ReactNode } from 'react'
import { List, ListRow, ListToggle, ListGroupHeader, ListSublist, ListEmpty } from 'admination-react'

export type ListShellProperty<T> = {

  id: string

  value: (row: T) => ReactNode
}

export function ListShell<T>({
  rows,
  filter,
  title,
  properties,
  rowKey,
  rowTestId,
  onRowClick,
  selectedKey,
  emptyText = 'Keine Einträge',
  groupBy,
  groups,
  cards = false,
  renderExpansion,
  rowAction,
}: {

  rows: readonly T[]

  filter?: (row: T) => boolean

  title: (row: T) => ReactNode

  properties?: ListShellProperty<T>[]

  rowKey: (row: T) => string

  rowTestId?: (row: T) => string | undefined
  onRowClick?: (row: T, e: React.MouseEvent<HTMLElement>) => void

  selectedKey?: string | null
  emptyText?: string

  groupBy?: (row: T) => string

  groups?: { id: string; header: ReactNode; trailing?: ReactNode }[]

  cards?: boolean

  renderExpansion?: (row: T) => ReactNode

  rowAction?: (row: T) => ReactNode
}) {

  const listClass = [cards && 'list--cards', rowAction && 'list--actions'].filter(Boolean).join(' ') || undefined
  const visible = filter ? rows.filter(filter) : rows

  const [collapsed, setCollapsed] = useState<Set<string>>(new Set())
  const toggleGroup = (id: string) => {
    const next = new Set(collapsed)
    if (next.has(id)) next.delete(id)
    else next.add(id)
    return next
  }

  const [expanded, setExpanded] = useState<Set<string>>(new Set())
  const toggleExpand = (key: string) => {
    const next = new Set(expanded)
    if (next.has(key)) next.delete(key)
    else next.add(key)
    return next
  }

  const renderRow = (row: T) => {
    const key = rowKey(row)
    const selected = selectedKey != null && key === selectedKey
    const clickable = !!onRowClick
    const isExpanded = expanded.has(key)

    const rowEl = (
      <ListRow
        key={renderExpansion ? undefined : key}
        role="listitem"
        data-testid={rowTestId?.(row)}
        tabIndex={clickable ? 0 : undefined}
        aria-selected={selected || undefined}
        isClickable={clickable}
        isSelected={selected}
        toggle={
          renderExpansion ? (
            <ListToggle
              center
              open={isExpanded}
              tabIndex={-1}
              aria-label={isExpanded ? 'Einklappen' : 'Ausklappen'}
              onClick={(e) => e.stopPropagation()}
              onToggle={() => setExpanded(toggleExpand(key))}
            />
          ) : undefined
        }
        title={title(row)}
        props={properties && properties.length > 0 ? properties.map((p) => <span key={p.id}>{p.value(row)}</span>) : undefined}
        action={rowAction?.(row)}
        onClick={clickable ? (e) => onRowClick(row, e) : undefined}
      />
    )

    if (!renderExpansion) return rowEl

    return (
      <div className="list-item" key={key}>
        {rowEl}
        {isExpanded ? <ListSublist>{renderExpansion(row)}</ListSublist> : null}
      </div>
    )
  }

  if (groupBy && groups) {
    const sections = groups
      .map((group) => ({
        group,
        groupRows: visible.filter((row) => groupBy(row) === group.id),
      }))
      .filter(({ groupRows }) => groupRows.length > 0)

    if (sections.length === 0) {
      return <ListEmpty>{emptyText}</ListEmpty>
    }

    return (
      <>
        {sections.map(({ group, groupRows }) => {
          const isCollapsed = collapsed.has(group.id)
          return (
            <section className="section" key={group.id}>
              <ListGroupHeader
                open={!isCollapsed}
                onToggle={() => setCollapsed(toggleGroup(group.id))}
                toggleLabel={isCollapsed ? 'Gruppe ausklappen' : 'Gruppe einklappen'}
                trailing={group.trailing}
                onClick={() => setCollapsed(toggleGroup(group.id))}

              >
                {group.header}
              </ListGroupHeader>
              {!isCollapsed && (
                <div className="section-body">
                  <List className={listClass} role="list">
                    {groupRows.map(renderRow)}
                  </List>
                </div>
              )}
            </section>
          )
        })}
      </>
    )
  }

  if (visible.length === 0) {
    return <ListEmpty>{emptyText}</ListEmpty>
  }

  return (
    <List className={listClass} role="list">
      {visible.map(renderRow)}
    </List>
  )
}
